import React, {useMemo} from 'react';

import {FeaturesEnum} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {getOdontoStepPosition} from 'presentation/modules/booking/odonto/utils/getOdontoStepPosition';
import {FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {Location} from 'presentation/modules/booking/shared/ui/pages/Location';
import {OdontoBookingStepsProps} from './types';
import {OdontoSpecialtySelection} from '../OdontoSpecialtySelection';
import {OdontoDistrictSelection} from '../OdontoDistrictSelection';
import {OdontoUnits} from '../OdontoUnits';
import {OdontoProfessionalSelection} from '../OdontoProfessionalSelection';
import {OdontoReviewBooking} from '../OdontoReviewBooking';
import {OdontoReviewBookingNew} from '../OdontoReviewBookingNew';

export function OdontoStepsWrapper({
	activeStep,
	handleBack,
	handleNext,
	stepLabels,
}: OdontoBookingStepsProps) {
	const bookingData = useBookingStore(state => state.bookingData);
	const {isManualLocation} = bookingData;
	const FLOW_TYPE = FlowEnum.ODONTO;
	const {isEnabled: isNewResumePageEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_NEW_RESUME_BOOKING_PAGE,
	);

	const stepPositions = useMemo(
		() => getOdontoStepPosition({isManualLocation}),
		[isManualLocation],
	);

	const {
		LOCATION_STEP,
		SPECIALITY_OR_EXAM_STEP,
		DISTRICT_STEP,
		UNIT_STEP,
		PROFESSIONAL_STEP,
		REVIEW_STEP,
	} = stepPositions;

	const stepContent = useMemo(() => {
		const baseSteps: Record<number, JSX.Element | null> = {
			[LOCATION_STEP]: <Location flowType={FLOW_TYPE} />,
			[SPECIALITY_OR_EXAM_STEP]: <OdontoSpecialtySelection handleNextStep={handleNext} />,
			[UNIT_STEP]: <OdontoUnits handleNextStep={handleNext} />,
			[PROFESSIONAL_STEP]: <OdontoProfessionalSelection handleNextStep={handleNext} />,
			[REVIEW_STEP]: isNewResumePageEnabled ? (
				<OdontoReviewBookingNew handlePrevStep={handleBack} />
			) : (
				<OdontoReviewBooking stepLabels={stepLabels} handlePrevStep={handleBack} />
			),
		};

		if (isManualLocation) {
			baseSteps[DISTRICT_STEP] = <OdontoDistrictSelection handleNextStep={handleNext} />;
		}

		return baseSteps;
	}, [
		LOCATION_STEP,
		SPECIALITY_OR_EXAM_STEP,
		DISTRICT_STEP,
		UNIT_STEP,
		PROFESSIONAL_STEP,
		REVIEW_STEP,
		isManualLocation,
		isNewResumePageEnabled,
		handleNext,
		handleBack,
		activeStep,
		stepLabels,
	]);

	return stepContent[activeStep] ?? null;
}
